// src/components/PengirimanPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaUpload, FaCalendarAlt, FaFilter, FaEye, FaCheckCircle } from "react-icons/fa";
import api from "../api";
import "./PengirimanPage.css";

const PER_PAGE = 20;
const BIG_PER_PAGE = 5000;

const MONTHS_ID = [
  "Januari","Februari","Maret","April","Mei","Juni",
  "Juli","Agustus","September","Oktober","November","Desember",
];

const MONTHS_ID_UP = [
  "JANUARI","FEBRUARI","MARET","APRIL","MEI","JUNI",
  "JULI","AGUSTUS","SEPTEMBER","OKTOBER","NOVEMBER","DESEMBER",
];

const ALL = -1;
const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);

const firstDayStr = (y, mIdx) => `${y}-${String(mIdx + 1).padStart(2, "0")}-01`;
const lastDayStr = (y, mIdx) => {
  const last = new Date(y, mIdx + 1, 0).getDate();
  return `${y}-${String(mIdx + 1).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
};

/** parsing aman: ISO, "dd/mm/yyyy", atau "15/1/2026, 12.28.05" */
const parseDateFlexible = (s) => {
  if (!s) return null;

  // coba Date native dulu
  let d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d;

  const str = String(s).trim();

  // dd/mm/yyyy atau dd-mm-yyyy
  let m = str.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
  if (m) {
    const [, dd, mm, yyyy] = m;
    d = new Date(`${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}T00:00:00`);
    if (!Number.isNaN(d.getTime())) return d;
  }

  // dd/mm/yyyy, HH.MM.SS (hasil toLocaleString id-ID)
  m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,\s*(\d{1,2})[.:](\d{1,2})[.:](\d{1,2}))?$/);
  if (m) {
    const dd = m[1], mm = m[2], yyyy = m[3];
    const hh = m[4] ?? "00";
    const mi = m[5] ?? "00";
    const ss = m[6] ?? "00";
    d = new Date(
      `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}T${String(hh).padStart(2, "0")}:${String(mi).padStart(2, "0")}:${String(ss).padStart(2, "0")}`
    );
    if (!Number.isNaN(d.getTime())) return d;
  }

  return null;
};

const firstNonEmpty = (...v) =>
  v.find((x) => x !== undefined && x !== null && String(x).trim() !== "");

const normalizeWorkArea = (wa) =>
  !wa ? "-" : typeof wa === "string" ? wa : wa?.name || "-";

/* ===== Status ===== */
const canonStatus = (s) => {
  const x = String(s || "").toLowerCase().trim();
  if (["pending", "menunggu", "submitted"].includes(x)) return "pending";
  if (["approved", "disetujui"].includes(x)) return "approved";
  if (["shipped", "sent", "terkirim", "dikirim"].includes(x)) return "shipped";
  if (["delivered", "received", "diterima", "telah diterima"].includes(x)) return "delivered";
  return x || "-";
};

const uiStatusLabel = (s, isAdmin) => {
  const x = canonStatus(s);
  if (x === "approved") return isAdmin ? "Segera Kirimkan" : "Akan dikirimkan";
  if (x === "pending") return isAdmin ? "Segera Kirimkan" : "Akan dikirimkan";
  if (x === "shipped") return "Telah dikirimkan";
  if (x === "delivered") return "Telah diterima";
  return s || "-";
};

const badgeClass = (s) => {
  const x = canonStatus(s);
  if (x === "approved" || x === "pending") return "warn";
  if (x === "shipped") return "info";
  if (x === "delivered") return "ok";
  return "muted";
};

const fmtDateTime = (v) => {
  if (!v || v === "-") return "-";
  const d = parseDateFlexible(v);
  if (!d) return String(v);
  return d.toLocaleString("id-ID");
};

function safeJsonParse(val) {
  try {
    if (!val) return null;
    if (Array.isArray(val)) return val;
    if (typeof val === "object") return val;
    const s = String(val).trim();
    if (!s) return null;
    if (!(s.startsWith("{") || s.startsWith("["))) return null;
    return JSON.parse(s);
  } catch {
    return null;
  }
}

// ✅ untuk HTML print agar aman
function escapeHtml(str = "") {
  return String(str)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

/* =========================
   ✅ FIX UTAMA: parsing is_ok & reason agar tidak nyebar
   ========================= */
const toTriBool = (v) => {
  if (v === true || v === false) return v;
  if (typeof v === "number") return v === 1 ? true : v === 0 ? false : null;

  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return null;

  if (["true", "1", "yes", "ya", "y", "ok", "sesuai"].includes(s)) return true;
  if (["false", "0", "no", "tidak", "n", "not_ok", "tidak_sesuai", "tidak sesuai"].includes(s)) return false;

  return null;
};

// parse receipt_note: "charger: 2 rusak | vaksin: botol pecah"
const parseReasonMap = (raw) => {
  const map = new Map();
  const s = String(raw ?? "").trim();
  if (!s || s === "-") return map;

  const parts = s.split("|").map((p) => p.trim()).filter(Boolean);
  for (const p of parts) {
    const idx = p.indexOf(":");
    if (idx > 0) {
      const key = p.slice(0, idx).trim().toLowerCase();
      const val = p.slice(idx + 1).trim();
      if (key) map.set(key, val || "-");
    }
  }
  return map;
};

// kalau reason sudah keburu "charger: 2 rusak", ambil kanan ":" saja
const cleanInlineReason = (reason, itemName) => {
  const r = String(reason ?? "").trim();
  if (!r || r === "-") return "-";

  const idx = r.indexOf(":");
  if (idx > 0) {
    const left = r.slice(0, idx).trim().toLowerCase();
    const right = r.slice(idx + 1).trim();
    if (left === String(itemName ?? "").trim().toLowerCase()) return right || "-";
  }
  return r;
};

/* ===== Normalisasi row dari endpoint /shipments ===== */
const normalizeShipmentRow = (raw) => {
  const shippingProofRaw = firstNonEmpty(
    raw?.shipping_proof_url,
    raw?.shipment_proof_url,
    raw?.proof_shipping_url,
    raw?.proof_of_shipping
  );

  const receiptProofRaw = firstNonEmpty(
    raw?.receipt_proof_url,
    raw?.receiving_proof_url,
    raw?.proof_receipt_url,
    raw?.proof_of_receipt
  );

  const receiptItems =
    safeJsonParse(raw?.receipt_items_result) ||
    safeJsonParse(raw?.receipt_items) ||
    (Array.isArray(raw?.receipt_items_result) ? raw.receipt_items_result : null) ||
    (Array.isArray(raw?.receipt_items) ? raw.receipt_items : null) ||
    null;

  const receiptEvidence =
    safeJsonParse(raw?.receipt_evidence) ||
    (Array.isArray(raw?.receipt_evidence) ? raw.receipt_evidence : null) ||
    null;

  return {
    submissionNumber: firstNonEmpty(raw?.submission_number, raw?.request_number, raw?.cart_number) || "-",
    submissionDate: firstNonEmpty(raw?.submission_date, raw?.request_date, raw?.date) || "-",

    shippingNumber: firstNonEmpty(raw?.number, raw?.shipping_number) || "-",
    shippingDate: firstNonEmpty(raw?.shipping_date) || "-",

    receivedAt: firstNonEmpty(raw?.received_at) || "-",
    receiverName: firstNonEmpty(raw?.receiver_name) || "-",
    receiverNip: firstNonEmpty(raw?.receiver_nip) || "-",
    receiptType: firstNonEmpty(raw?.receipt_type) || "-",
    receiptNote: firstNonEmpty(raw?.receipt_note) || "-",

    receiptItems,
    receiptEvidence,

    userName: firstNonEmpty(raw?.user_name, raw?.user?.name) || "-",
    workArea: normalizeWorkArea(firstNonEmpty(raw?.work_area, raw?.user?.work_area)),

    shippingProof: shippingProofRaw,
    receiptProof: receiptProofRaw,

    status: raw?.status || raw?.shipment_status || raw?.state || "-",
  };
};

const slicePage = (arr, p) => {
  const start = (p - 1) * PER_PAGE;
  const end = p * PER_PAGE;
  return (arr || []).slice(start, end);
};

function receiptTypeLabel(t) {
  const x = String(t || "").toLowerCase();
  if (x === "sesuai") return "Diterima sesuai";
  if (x === "sebagian") return "Diterima sebagian";
  if (x === "tidak_sesuai") return "Tidak sesuai";
  return t && t !== "-" ? t : "-";
}

/** Build SBBK HTML (dibuka di tab baru) */
function buildSbbkHtml({
  nomorSbbk,
  workArea,
  shippedAtRaw,
  receivedAtRaw,
  receiverName,
  receiverNip,
  signatureUrl,
  items,
}) {
  const origin = window.location.origin || "";

  const shippedDateObj = parseDateFlexible(shippedAtRaw) || new Date();
  const day = String(shippedDateObj.getDate()).padStart(2, "0");
  const monthIdx = shippedDateObj.getMonth();
  const year = shippedDateObj.getFullYear();
  const monthName = MONTHS_ID_UP[monthIdx] || "JANUARI";
  const todayStr = `${day} ${monthName} ${year}`;

  const shippedAtText = shippedAtRaw && shippedAtRaw !== "-" ? fmtDateTime(shippedAtRaw) : "-";
  const receivedAtText = receivedAtRaw && receivedAtRaw !== "-" ? fmtDateTime(receivedAtRaw) : "-";

  const rowsHtml = (items || []).map((it, idx) => {
    const ok = it.is_ok === true;
    const notOk = it.is_ok === false;

    const photoCell = it.photo_url
      ? `<img src="${escapeHtml(it.photo_url)}" style="width:70px;height:70px;object-fit:cover;border:1px solid #e5e7eb;border-radius:6px;" />`
      : "-";

    return `
      <tr>
        <td style="border:1px solid #000; padding:4px; text-align:center;">${idx + 1}</td>
        <td style="border:1px solid #000; padding:4px;">${escapeHtml(it.name || "-")}</td>
        <td style="border:1px solid #000; padding:4px; text-align:center;">${escapeHtml(it.qty_sent ?? "-")}</td>
        <td style="border:1px solid #000; padding:4px; text-align:center;">${escapeHtml(it.qty_received ?? "-")}</td>
        <td style="border:1px solid #000; padding:4px; text-align:center;">${ok ? "✓" : ""}</td>
        <td style="border:1px solid #000; padding:4px; text-align:center;">${notOk ? "✗" : ""}</td>
        <td style="border:1px solid #000; padding:4px;">${escapeHtml(it.reason || "-")}</td>
        <td style="border:1px solid #000; padding:4px; text-align:center;">${photoCell}</td>
      </tr>
    `;
  }).join("");

  const signatureImg = signatureUrl
    ? `<img src="${escapeHtml(signatureUrl)}" alt="TTD Penerima" style="height:70px;max-width:240px;object-fit:contain;" />`
    : `<div style="height:70px;"></div>`;

  return `
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>SBBK ${escapeHtml(nomorSbbk || "")}</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; font-family: "Times New Roman", serif; font-size: 11pt; color:#000; }
    .page { width: 210mm; min-height: 297mm; margin: 10mm auto; padding: 10mm 15mm 15mm 15mm; }
    .kop-wrapper { text-align: center; margin-bottom: 6mm; }
    .kop-wrapper img { width: 100%; height: auto; max-height: 95px; }
    h1 { font-size: 13pt; text-align: center; margin: 0; font-weight: bold; }
    .nomor { text-align: center; font-size: 10pt; margin-top: 2px; margin-bottom: 4mm; }
    .paragraf { font-size: 10pt; text-align: left; margin-bottom: 3mm; line-height: 1.4; }
    table { border-collapse: collapse; width: 100%; font-size: 9.5pt; }
    thead td { font-weight: bold; text-align: center; }
    .ttd-wrapper { width: 100%; margin-top: 10mm; font-size: 10pt; }
    .meta { margin: 0 0 8px 0; font-size: 10pt; }
    .meta b { display:inline-block; min-width: 130px; }
    @page { size: A4; margin: 10mm 15mm 15mm 15mm; }
    @media print { .no-print { display: none !important; } }
    .printbar { display:flex; justify-content:flex-end; gap:8px; margin-bottom:10px; }
    .btn { padding:8px 12px; border:1px solid #111; background:#fff; cursor:pointer; border-radius:8px; font-family: Arial, sans-serif; font-size: 10pt; }
  </style>
</head>
<body>
  <div class="page">
    <div class="printbar no-print">
      <button class="btn" onclick="window.print()">Cetak</button>
      <button class="btn" onclick="window.close()">Tutup</button>
    </div>

    <div class="kop-wrapper">
      <img src="${origin}/kop.png" alt="Kop SBBK" />
    </div>

    <h1>SURAT BUKTI BARANG KELUAR (SBBK)</h1>
    <div class="nomor">Nomor : ${escapeHtml(nomorSbbk || "-")}</div>

    <div class="meta">
      <div><b>Wilayah Kerja</b>: ${escapeHtml(workArea || "-")}</div>
      <div><b>Dikirim</b>: ${escapeHtml(shippedAtText)}</div>
      <div><b>Diterima</b>: ${escapeHtml(receivedAtText)}</div>
    </div>

    <div class="paragraf">
      Pada hari ini tanggal ${day}, bulan ${monthName} Tahun ${year}, telah dilakukan pengiriman barang persediaan berupa barang habis pakai untuk dipergunakan pada <strong>${escapeHtml(workArea || "-")}</strong>.
      Adapun rincian barang <strong>(qty dikirim vs qty diterima)</strong> serta hasil validasi penerimaan adalah sebagai berikut:
    </div>

    <table>
      <thead>
        <tr>
          <td style="border:1px solid #000; width:5%;">NO</td>
          <td style="border:1px solid #000;">NAMA BARANG</td>
          <td style="border:1px solid #000; width:10%;">QTY DIKIRIM</td>
          <td style="border:1px solid #000; width:10%;">QTY DITERIMA</td>
          <td style="border:1px solid #000; width:7%;">SESUAI</td>
          <td style="border:1px solid #000; width:10%;">TIDAK SESUAI</td>
          <td style="border:1px solid #000; width:18%;">ALASAN</td>
          <td style="border:1px solid #000; width:14%;">BUKTI FOTO</td>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml || `<tr><td colspan="8" style="border:1px solid #000; padding:8px; text-align:center;">Tidak ada data item.</td></tr>`}
      </tbody>
    </table>

    <table class="ttd-wrapper">
      <tr>
        <td style="width:50%; vertical-align:top;">
          Yang Menerima,<br/>
          <div style="margin-top:10px;">${signatureImg}</div>
          <div style="margin-top:6px;"><strong>${escapeHtml(receiverName || "-")}</strong></div>
          <div style="margin-top:2px;">NIP: ${escapeHtml(receiverNip || "-")}</div>
          <div style="margin-top:2px; font-size:9pt;">(Tgl/Jam terima: ${escapeHtml(receivedAtText)})</div>
        </td>
        <td style="width:50%; vertical-align:top; text-align:center;">
          Pangkalpinang, ${todayStr}<br/>
          Yang Menyerahkan,<br/><br/>
          <img src="${origin}/ttd-devi.png" alt="Tanda tangan" style="height:60px;"/><br/>
          <strong>Devi Andriani Purnamasari</strong><br/>
          NIP: 199002052022032001
        </td>
      </tr>
    </table>
  </div>
</body>
</html>
`;
}

/* =========================
   Modal Konfirmasi Terima
   ========================= */

function dataURLtoBlob(dataURL) {
  const parts = String(dataURL || "").split(",");
  const mime = parts[0]?.match(/:(.*?);/)?.[1] || "image/png";
  const bstr = atob(parts[1] || "");
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new Blob([u8arr], { type: mime });
}

function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    drawing.current = true;
    last.current = getPos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const p = getPos(e);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;

    const url = canvas.toDataURL("image/png");
    onChange(url);
  };

  const end = () => {
    drawing.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange("");
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = 900;
    canvas.height = 180;

    if (value) {
      const img = new Image();
      img.onload = () => {
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
      };
      img.src = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10 }}>
      <div style={{ width: "100%", overflow: "auto" }}>
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: 180, background: "#fff", borderRadius: 8 }}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={(e) => { e.preventDefault(); start(e); }}
          onTouchMove={(e) => { e.preventDefault(); move(e); }}
          onTouchEnd={(e) => { e.preventDefault(); end(); }}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
        <button type="button" className="sh-btn sh-btn-light sh-btn-sm" onClick={clear}>
          Bersihkan TTD
        </button>
        <div style={{ fontSize: 12, opacity: 0.8 }}>
          {value ? "TTD terisi ✅" : "Silakan gambar tanda tangan di atas."}
        </div>
      </div>
    </div>
  );
}

function ConfirmReceiptModal({ open, onClose, shipmentRow, submissionItems, onSuccess }) {
  const [receiverName, setReceiverName] = useState("");
  const [receiverNip, setReceiverNip] = useState("");
  const [items, setItems] = useState([]);
  const [signature, setSignature] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setReceiverName("");
    setReceiverNip("");
    setSignature("");

    const init = (submissionItems || []).map((it) => ({
      product_id: Number(it.product_id ?? it.id ?? 0) || null,
      name: it.name || "(Barang)",
      qty_sent: Number(it.qty ?? 0),
      qty_received: Number(it.qty ?? 0),
      is_ok: true,
      reason: "",
      photo_file: null,
      photo_preview: "",
    }));
    setItems(init);
  }, [open, submissionItems]);

  const receiptTypeAuto = useMemo(() => {
    if (!items.length) return "sesuai";
    const okCount = items.filter((x) => x.is_ok === true).length;
    if (okCount === items.length) return "sesuai";
    if (okCount === 0) return "tidak_sesuai";
    return "sebagian";
  }, [items]);

  const setRow = (idx, patch) => {
    setItems((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const validate = () => {
    if (!receiverName.trim()) return "Nama penerima wajib diisi.";
    if (!signature) return "TTD Digital wajib diisi.";

    for (const it of items) {
      const qtyR = Number(it.qty_received);
      if (!Number.isFinite(qtyR) || qtyR < 0) return `Qty diterima tidak valid untuk item: ${it.name}`;

      if (it.is_ok === false) {
        if (!String(it.reason || "").trim()) return `Alasan wajib diisi untuk item: ${it.name}`;
        if (!it.photo_file) return `Foto bukti wajib diisi untuk item: ${it.name}`;
      }
    }
    return null;
  };

  const submit = async () => {
    const msg = validate();
    if (msg) return alert(msg);

    setSaving(true);
    try {
      const number =
        shipmentRow?.shippingNumber && shipmentRow.shippingNumber !== "-"
          ? shipmentRow.shippingNumber
          : shipmentRow?.submissionNumber;

      const fd = new FormData();
      fd.append("receiver_name", receiverName.trim());
      if (receiverNip.trim()) fd.append("receiver_nip", receiverNip.trim());
      fd.append("receipt_type", receiptTypeAuto);

      const note = items
        .filter((x) => x.is_ok === false)
        .map((x) => `${x.name}: ${x.reason}`)
        .join(" | ");
      if (note) fd.append("receipt_note", note);

      const sigBlob = dataURLtoBlob(signature);
      fd.append("signature", sigBlob, "signature.png");

      const resultPayload = items.map((x) => ({
        product_id: x.product_id,
        name: x.name,
        qty_sent: Number(x.qty_sent ?? 0),
        qty_received: Number(x.qty_received ?? 0),
        is_ok: x.is_ok === true,
        reason: x.is_ok === false ? (x.reason || "") : "-",
      }));
      fd.append("receipt_items_result", JSON.stringify(resultPayload));

      items.forEach((x, idx) => {
        if (x.is_ok === false && x.photo_file) {
          fd.append(`evidence_files[${idx}]`, x.photo_file, x.photo_file.name || `evidence_${idx}.jpg`);
          fd.append(`evidence_meta[${idx}]`, JSON.stringify({ product_id: x.product_id, name: x.name }));
        }
      });

      await api.post(`/shipments/${encodeURIComponent(number)}/confirm-receipt`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      onSuccess?.();
      onClose?.();
    } catch (e) {
      alert(e?.response?.data?.message || "Gagal konfirmasi terima.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="sh-modalOverlay">
      <div className="sh-modal">
        <div className="sh-modalHead">
          <div>
            <div className="sh-modalTitle">Konfirmasi Terima</div>
            <div className="sh-modalSub">
              Kondisi otomatis: <b>{receiptTypeLabel(receiptTypeAuto)}</b> (berdasarkan checklist item)
            </div>
          </div>
          <button className="sh-btn sh-btn-light sh-btn-sm" onClick={onClose}>Tutup</button>
        </div>

        <div className="sh-modalBody">
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Detail Barang yang Dikirim</div>

          <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 12 }}>
            <table className="sh-table" style={{ minWidth: 980 }}>
              <thead>
                <tr>
                  <th style={{ width: 50 }}>No</th>
                  <th>Nama Barang</th>
                  <th style={{ width: 120 }}>Qty Dikirim</th>
                  <th style={{ width: 140 }}>Qty Diterima</th>
                  <th style={{ width: 110 }}>Sesuai</th>
                  <th style={{ width: 130 }}>Tidak Sesuai</th>
                  <th style={{ width: 220 }}>Alasan</th>
                  <th style={{ width: 200 }}>Bukti Foto</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx}>
                    <td className="sh-center">{idx + 1}</td>
                    <td>{it.name}</td>
                    <td className="sh-center">{it.qty_sent}</td>
                    <td className="sh-center">
                      <input
                        type="number"
                        min="0"
                        value={it.qty_received}
                        onChange={(e) => setRow(idx, { qty_received: e.target.value })}
                        style={{ width: 90, padding: 6, borderRadius: 8, border: "1px solid #d1d5db" }}
                      />
                    </td>
                    <td className="sh-center">
                      <input
                        type="checkbox"
                        checked={it.is_ok === true}
                        onChange={() => setRow(idx, { is_ok: true, reason: "", photo_file: null, photo_preview: "" })}
                      />
                    </td>
                    <td className="sh-center">
                      <input
                        type="checkbox"
                        checked={it.is_ok === false}
                        onChange={() => setRow(idx, { is_ok: false })}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        placeholder="-"
                        value={it.reason}
                        disabled={it.is_ok !== false}
                        onChange={(e) => setRow(idx, { reason: e.target.value })}
                        style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #d1d5db" }}
                      />
                    </td>
                    <td className="sh-center">
                      <input
                        type="file"
                        accept="image/*"
                        disabled={it.is_ok !== false}
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          if (!file) return setRow(idx, { photo_file: null, photo_preview: "" });
                          const url = URL.createObjectURL(file);
                          setRow(idx, { photo_file: file, photo_preview: url });
                        }}
                      />
                      {it.photo_preview ? (
                        <div style={{ marginTop: 6 }}>
                          <img
                            src={it.photo_preview}
                            alt="preview"
                            style={{ width: 70, height: 70, objectFit: "cover", borderRadius: 8, border: "1px solid #e5e7eb" }}
                          />
                        </div>
                      ) : (
                        <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>-</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ fontSize: 12, marginTop: 8, opacity: 0.8 }}>
            * Jika item <b>Tidak Sesuai</b>, maka <b>Alasan</b> dan <b>Foto bukti</b> wajib diisi.
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={{ fontWeight: 700 }}>Nama Penerima (wajib)</label>
            <input
              value={receiverName}
              onChange={(e) => setReceiverName(e.target.value)}
              placeholder="Masukkan nama penerima"
              style={{ width: "100%", marginTop: 6, padding: 10, borderRadius: 10, border: "1px solid #d1d5db" }}
            />
          </div>

          <div style={{ marginTop: 12 }}>
            <label style={{ fontWeight: 700 }}>NIP Penerima (opsional)</label>
            <input
              value={receiverNip}
              onChange={(e) => setReceiverNip(e.target.value)}
              placeholder="Masukkan NIP (boleh kosong)"
              style={{ width: "100%", marginTop: 6, padding: 10, borderRadius: 10, border: "1px solid #d1d5db" }}
            />
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={{ fontWeight: 700 }}>TTD Digital (wajib)</label>
            <div style={{ marginTop: 8 }}>
              <SignaturePad value={signature} onChange={setSignature} />
            </div>
          </div>
        </div>

        <div className="sh-modalFoot">
          <button className="sh-btn sh-btn-light" onClick={onClose} disabled={saving}>Batal</button>
          <button className="sh-btn sh-btn-primary" onClick={submit} disabled={saving}>
            <FaCheckCircle /> {saving ? "Menyimpan..." : "Konfirmasi Terima"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================
   Page
   ========================= */

export default function PengirimanPage({ userRole = "regular", shipments: shipmentsProp }) {
  const isAdmin = String(userRole).toLowerCase() === "admin";

  const [monthIdx, setMonthIdx] = useState(ALL);
  const [year, setYear] = useState(ALL);

  const [rows, setRows] = useState([]);
  const [fullRows, setFullRows] = useState(null);
  const [loading, setLoading] = useState(!Array.isArray(shipmentsProp));
  const [err, setErr] = useState("");

  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);

  const [openConfirm, setOpenConfirm] = useState(false);
  const [confirmRow, setConfirmRow] = useState(null);
  const [confirmSubmissionItems, setConfirmSubmissionItems] = useState([]);

  const username = useMemo(
    () =>
      localStorage.getItem("username") ||
      localStorage.getItem("user_name") ||
      localStorage.getItem("email") ||
      null,
    []
  );
  const userId = useMemo(() => localStorage.getItem("user_id"), []);

  const filtersActive = year !== ALL || monthIdx !== ALL;

  const bulanLabel =
    year === ALL && monthIdx === ALL
      ? "Semua Periode"
      : year !== ALL && monthIdx === ALL
      ? `Tahun ${year}`
      : year === ALL && monthIdx !== ALL
      ? `${MONTHS_ID[monthIdx]} (Semua Tahun)`
      : `${MONTHS_ID[monthIdx]} ${year}`;

  const getFilterDate = (r) => {
    const sd = r?.shippingDate;
    if (sd && sd !== "-" && String(sd).trim() !== "") return sd;
    const sub = r?.submissionDate;
    if (sub && sub !== "-" && String(sub).trim() !== "") return sub;
    return null;
  };

  const matchPeriod = (dateStr) => {
    const d = parseDateFlexible(dateStr);
    if (!d) return false;

    if (year === ALL && monthIdx === ALL) return true;
    if (year !== ALL && monthIdx === ALL) return d.getFullYear() === year;
    if (year === ALL && monthIdx !== ALL) return d.getMonth() === monthIdx;
    return d.getFullYear() === year && d.getMonth() === monthIdx;
  };

  const applyFiltersClient = (list) => {
    if (!filtersActive) return list;
    return (list || []).filter((r) => {
      const dt = getFilterDate(r);
      return dt ? matchPeriod(dt) : false;
    });
  };

  const baseScope = useMemo(() => {
    return isAdmin
      ? { scope: "all" }
      : {
          scope: "mine",
          ...(username ? { username } : {}),
          ...(userId ? { user_id: userId } : {}),
        };
  }, [isAdmin, username, userId]);

  // ===== LOAD ALL (untuk filter aktif) =====
  const loadAllForFilter = useCallback(async () => {
    setLoading(true);
    setErr("");

    try {
      if (Array.isArray(shipmentsProp)) {
        const normalized = shipmentsProp.map(normalizeShipmentRow);
        const filtered = applyFiltersClient(normalized);

        setFullRows(filtered);
        setTotal(filtered.length);
        setLastPage(Math.max(1, Math.ceil(filtered.length / PER_PAGE)));
        setRows(slicePage(filtered, 1));
        return;
      }

      const params = {
        ...baseScope,
        page: 1,
        per_page: BIG_PER_PAGE,
        all: 1,
      };

      if (monthIdx !== ALL) params.month = monthIdx + 1;
      if (year !== ALL) params.year = year;

      if (year !== ALL && monthIdx !== ALL) {
        params.start_date = firstDayStr(year, monthIdx);
        params.end_date = lastDayStr(year, monthIdx);
      } else if (year !== ALL && monthIdx === ALL) {
        params.start_date = `${year}-01-01`;
        params.end_date = `${year}-12-31`;
      }

      const { data } = await api.get("/shipments", { params });

      const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.shipments)
        ? data.shipments
        : [];

      const normalized = arr.map(normalizeShipmentRow);
      const filtered = applyFiltersClient(normalized);

      setFullRows(filtered);
      setTotal(filtered.length);
      setLastPage(Math.max(1, Math.ceil(filtered.length / PER_PAGE)));
      setRows(slicePage(filtered, 1));
    } catch (e) {
      setRows([]);
      setFullRows([]);
      setTotal(0);
      setLastPage(1);
      setErr(e?.response?.data?.message || e?.message || "Gagal memuat data pengiriman.");
    } finally {
      setLoading(false);
    }
  }, [shipmentsProp, baseScope, year, monthIdx]);

  // ===== LOAD PAGE (untuk semua periode) =====
  const loadPage = useCallback(
    async (targetPage) => {
      setLoading(true);
      setErr("");

      try {
        if (Array.isArray(shipmentsProp)) {
          const normalized = shipmentsProp.map(normalizeShipmentRow);
          setFullRows(normalized);
          setTotal(normalized.length);
          setLastPage(Math.max(1, Math.ceil(normalized.length / PER_PAGE)));
          setRows(slicePage(normalized, targetPage));
          return;
        }

        const params = { ...baseScope, page: targetPage, per_page: PER_PAGE };
        const { data, headers } = await api.get("/shipments", { params });

        const arr = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.shipments)
          ? data.shipments
          : [];

        const normalized = arr.map(normalizeShipmentRow);

        const meta = data?.meta || data?.pagination || null;
        const headerTotal = Number(headers?.["x-total-count"]);
        const totalFromServer =
          (meta && Number(meta.total) >= 0 ? Number(meta.total) : null) ??
          (Number.isFinite(headerTotal) ? headerTotal : null) ??
          normalized.length;

        const computedLast = Math.max(1, Math.ceil(Number(totalFromServer || 0) / PER_PAGE));
        const serverNotPaginating = normalized.length > PER_PAGE;

        if (serverNotPaginating) {
          setFullRows(normalized);
          setTotal(normalized.length);
          setLastPage(Math.max(1, Math.ceil(normalized.length / PER_PAGE)));
          setRows(slicePage(normalized, targetPage));
        } else {
          setFullRows(null);
          setTotal(totalFromServer);
          setLastPage(computedLast);
          setRows(normalized);
        }
      } catch (e) {
        setRows([]);
        setFullRows(null);
        setTotal(0);
        setLastPage(1);
        setErr(e?.response?.data?.message || e?.message || "Gagal memuat data pengiriman.");
      } finally {
        setLoading(false);
      }
    },
    [shipmentsProp, baseScope]
  );

  useEffect(() => setPage(1), [monthIdx, year]);

  useEffect(() => {
    if (filtersActive) loadAllForFilter();
    else loadPage(1);
  }, [filtersActive, loadAllForFilter, loadPage]);

  useEffect(() => {
    if (filtersActive) {
      setRows(slicePage(fullRows || [], page));
      return;
    }
    if (Array.isArray(fullRows)) {
      setRows(slicePage(fullRows, page));
      return;
    }
    loadPage(page);
  }, [page, filtersActive, fullRows, loadPage]);

  const openUrl = (url) => url && window.open(url, "_blank", "noopener,noreferrer");

  const effectiveNumber = (r) =>
    r.shippingNumber && r.shippingNumber !== "-" ? r.shippingNumber : r.submissionNumber;

  const triggerUpload = (type, number) => {
    if (!number || number === "-") return;
    setUploadTarget({ type, number });
    fileInputRef.current?.click();
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !uploadTarget) return;

    const { type, number } = uploadTarget;
    try {
      const fd = new FormData();
      fd.append("file", file);

      const url =
        type === "shipping"
          ? `/shipments/${encodeURIComponent(number)}/upload-shipping`
          : `/shipments/${encodeURIComponent(number)}/upload-receipt`;

      await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });

      if (filtersActive) await loadAllForFilter();
      else await loadPage(page);
    } catch (err) {
      alert(err?.response?.data?.message || "Gagal mengunggah bukti.");
    } finally {
      setUploadTarget(null);
    }
  };

  /* =========================
     ✅ View SBBK (FIX: is_ok & reason per item)
     ========================= */
  const handleViewSbbk = async (row) => {
    try {
      const subNo = row.submissionNumber;
      if (!subNo || subNo === "-") return;

      // Ambil detail item dari submission (qty dikirim)
      let submissionItems = [];
      try {
        const res = await api.get(`/submissions/${encodeURIComponent(subNo)}`);
        const data = res?.data?.data || {};
        submissionItems = Array.isArray(data?.items) ? data.items : [];
      } catch {
        submissionItems = [];
      }

      // receiptItems hasil validasi per-item
      const receiptItems = Array.isArray(row.receiptItems) ? row.receiptItems : [];

      // ✅ fallback alasan dari receipt_note (level shipment)
      const noteMap = parseReasonMap(row.receiptNote);

      // Map receipt by product_id (utama) dan by name (fallback)
      const byPid = new Map();
      const byName = new Map();

      receiptItems.forEach((it) => {
        const pid = Number(it?.product_id ?? it?.id ?? 0) || null;
        const nm = String(it?.name || it?.product_name || "").trim().toLowerCase();
        if (pid) byPid.set(pid, it);
        if (nm) byName.set(nm, it);
      });

      const merged = (submissionItems || []).map((it) => {
        const pid = Number(it?.product_id ?? it?.id ?? 0) || null;
        const name = it?.name || "(Barang)";
        const sent = Number(it?.qty ?? 0);

        const keyName = String(name).trim().toLowerCase();
        const rcv = (pid && byPid.get(pid)) || byName.get(keyName) || null;

        // qty diterima
        const qtyReceivedRaw =
          rcv?.qty_received ??
          rcv?.qtyDiterima ??
          rcv?.qty_received_value ??
          rcv?.qty;

        const qtyReceived =
          qtyReceivedRaw !== undefined && qtyReceivedRaw !== null && qtyReceivedRaw !== ""
            ? Number(qtyReceivedRaw)
            : "-";

        // ✅ is_ok robust (boolean/string/number)
        const isOkFromItem = toTriBool(
          rcv?.is_ok ?? rcv?.sesuai ?? rcv?.isOk ?? rcv?.ok
        );

        // ✅ reason fallback: dari receipt_note per item, atau dari rcv.reason
        const reasonFromNote = noteMap.get(keyName) || null;

        const reasonRaw =
          rcv?.reason ?? rcv?.alasan ?? rcv?.note ?? "";

        // ✅ tentukan final ok/tidak:
        let isOk = isOkFromItem;
        if (isOk === null) isOk = reasonFromNote ? false : true;

        // ✅ cegah alasan global nyasar ke item lain
        const cleanedInline = cleanInlineReason(reasonRaw, name);

        // jika reason berbentuk "xxx: yyy" tapi xxx != nama item, maka jangan set tidak sesuai
        if (isOk === false && !reasonFromNote) {
          const rStr = String(reasonRaw || "").trim();
          const idx = rStr.indexOf(":");
          if (idx > 0) {
            const left = rStr.slice(0, idx).trim().toLowerCase();
            if (left && left !== keyName) isOk = true;
          }
        }

        const finalReason =
          isOk === false
            ? (reasonFromNote || cleanedInline || "-")
            : "-";

        const photoUrl =
          rcv?.photo_url ??
          rcv?.photoUrl ??
          rcv?.bukti_foto_url ??
          rcv?.buktiFotoUrl ??
          "";

        return {
          product_id: pid,
          name,
          qty_sent: sent,
          qty_received: qtyReceived,
          is_ok: isOk,
          reason: finalReason,
          photo_url: photoUrl || "",
        };
      });

      // ✅ FIX: parameter sesuai signature buildSbbkHtml (shippedAtRaw/receivedAtRaw)
      const html = buildSbbkHtml({
        nomorSbbk: subNo,
        workArea: row.workArea,
        shippedAtRaw: row.shippingDate,
        receivedAtRaw: row.receivedAt,
        receiverName: row.receiverName && row.receiverName !== "-" ? row.receiverName : "-",
        receiverNip: row.receiverNip && row.receiverNip !== "-" ? row.receiverNip : "-",
        signatureUrl: row.receiptProof || "",
        items: merged,
      });

      const w = window.open("", "_blank");
      if (!w) return alert("Popup diblokir browser. Izinkan popup untuk melihat SBBK.");
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e) {
      alert(e?.response?.data?.message || "Gagal membuka SBBK.");
    }
  };

  /** ✅ buka modal confirm (ambil item dari submission) */
  const openConfirmReceipt = async (row) => {
    try {
      const subNo = row.submissionNumber;
      if (!subNo || subNo === "-") return alert("Nomor pengajuan tidak valid.");

      const res = await api.get(`/submissions/${encodeURIComponent(subNo)}`);
      const data = res?.data?.data || {};
      const submissionItems = Array.isArray(data?.items) ? data.items : [];

      setConfirmRow(row);
      setConfirmSubmissionItems(submissionItems);
      setOpenConfirm(true);
    } catch (e) {
      alert(e?.response?.data?.message || "Gagal memuat detail barang pengajuan.");
    }
  };

  const canUploadShipping = (r) =>
    isAdmin && (canonStatus(r.status) === "approved" || canonStatus(r.status) === "pending");

  const canConfirmReceipt = (r) =>
    !isAdmin && canonStatus(r.status) === "shipped";

  const resetFilter = () => {
    setMonthIdx(ALL);
    setYear(ALL);
  };

  return (
    <div className="sh-page">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        style={{ display: "none" }}
        onChange={onFilePicked}
      />

      <ConfirmReceiptModal
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        shipmentRow={confirmRow}
        submissionItems={confirmSubmissionItems}
        onSuccess={async () => {
          if (filtersActive) await loadAllForFilter();
          else await loadPage(page);
        }}
      />

      <div className="sh-head">
        <div>
          <h2 className="sh-title">Pengiriman</h2>
          <div className="sh-subtitle">
            Periode: <b>{bulanLabel}</b> — Total data: <b>{total}</b>{" "}
            <span style={{ opacity: 0.7 }}>• Tampil: {rows.length}</span>
          </div>
        </div>
      </div>

      <div className="sh-toolbar">
        <div className="sh-filter">
          <FaCalendarAlt className="sh-ic" />
          <select
            className="sh-select"
            value={monthIdx}
            onChange={(e) => setMonthIdx(Number(e.target.value))}
            title="Filter Bulan"
          >
            <option value={ALL}>Semua Bulan</option>
            {MONTHS_ID.map((m, idx) => (
              <option key={m} value={idx}>{m}</option>
            ))}
          </select>
        </div>

        <div className="sh-filter">
          <FaFilter className="sh-ic" />
          <select
            className="sh-select"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            title="Filter Tahun"
          >
            <option value={ALL}>Semua Tahun</option>
            {YEAR_OPTIONS.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <button type="button" className="sh-btn sh-btn-light" onClick={resetFilter}>
          Reset
        </button>
      </div>

      {err && <div className="sh-alert">{err}</div>}

      <div className="sh-card">
        <div className="sh-tableWrap">
          <table className={`sh-table ${isAdmin ? "is-admin" : ""}`}>
            <thead>
              <tr>
                {isAdmin && <th>Nama</th>}
                {isAdmin && <th>Wilayah Kerja</th>}
                <th>Pengiriman</th>
                <th>Penerimaan</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={isAdmin ? 5 : 3} className="sh-center">Memuat...</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 5 : 3} className="sh-center">Tidak ada data.</td>
                </tr>
              ) : (
                rows.map((r, i) => {
                  const st = canonStatus(r.status);

                  return (
                    <tr key={`${r.shippingNumber}-${r.submissionNumber}-${i}`}>
                      {isAdmin && <td className="sh-ellipsis">{r.userName}</td>}
                      {isAdmin && <td className="sh-ellipsis">{r.workArea}</td>}

                      {/* Pengiriman */}
                      <td className="sh-center">
                        <div className="sh-actionsCell">
                          {r.shippingProof ? (
                            <button
                              type="button"
                              className="sh-btn sh-btn-outline sh-btn-sm"
                              onClick={() => openUrl(r.shippingProof)}
                            >
                              <FaEye /> Lihat
                            </button>
                          ) : (
                            <span>-</span>
                          )}

                          {isAdmin && (
                            <button
                              type="button"
                              className="sh-btn sh-btn-primary sh-btn-sm"
                              onClick={() => triggerUpload("shipping", effectiveNumber(r))}
                              disabled={!canUploadShipping(r)}
                              title="Upload bukti pengiriman (opsional)"
                            >
                              <FaUpload /> Konfirmasi Kirim
                            </button>
                          )}
                        </div>

                        <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
                          Dikirim: {r.shippingDate && r.shippingDate !== "-" ? fmtDateTime(r.shippingDate) : "-"}
                        </div>
                      </td>

                      {/* Penerimaan */}
                      <td className="sh-center">
                        {st === "delivered" && r.receiptProof ? (
                          <>
                            <button
                              type="button"
                              className="sh-btn sh-btn-outline sh-btn-sm"
                              onClick={() => handleViewSbbk(r)}
                              title="Buka dokumen SBBK digital (validasi penerimaan)"
                            >
                              <FaEye /> Lihat SBBK
                            </button>

                            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.9, textAlign: "left" }}>
                              <div>Penerima: <b>{r.receiverName || "-"}</b></div>
                              <div>NIP: {r.receiverNip || "-"}</div>
                              <div>Kondisi: <b>{receiptTypeLabel(r.receiptType)}</b></div>
                              <div>Terima: {r.receivedAt && r.receivedAt !== "-" ? fmtDateTime(r.receivedAt) : "-"}</div>
                            </div>
                          </>
                        ) : canConfirmReceipt(r) ? (
                          <button
                            type="button"
                            className="sh-btn sh-btn-primary sh-btn-sm"
                            onClick={() => openConfirmReceipt(r)}
                            title="Konfirmasi barang diterima"
                          >
                            <FaCheckCircle /> Konfirmasi Terima
                          </button>
                        ) : (
                          <div style={{ textAlign: "center" }}>-</div>
                        )}
                      </td>

                      <td className="sh-center">
                        <span className={`sh-badge ${badgeClass(r.status)}`}>
                          {uiStatusLabel(r.status, isAdmin)}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="sh-pager">
          <button
            className="sh-btn sh-btn-light"
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>

          <div className="sh-pagerInfo">
            Halaman <b>{page}</b> / <b>{lastPage}</b>
          </div>

          <button
            className="sh-btn sh-btn-light"
            type="button"
            disabled={page >= lastPage || loading}
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
